import React, { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { CompactView, Login, Modal as CVModal, assetType } from '@bynder/compact-view';
import { notify } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { getJWT } from '@bynder/js-access-token-lib';
import {
	getAssetExtension,
	isUnsupportedDocumentExtension,
	parseAsset,
	ReferenceAsset,
} from './utils';

interface Props {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly onSelect: (assets: ReferenceAsset[]) => void;
	readonly allowDocuments?: boolean;
	readonly maxAssets?: number;
}

export function ReferenceAssetPicker({
	isOpen,
	onClose,
	onSelect,
	allowDocuments,
	maxAssets,
}: Props) {
	const { translate } = useTranslate();
	const [accessToken, setAccessToken] = useState('');

	useEffect(() => {
		let isMounted = true;

		if (!isOpen) {
			setAccessToken('');

			return () => {
				isMounted = false;
			};
		}

		getJWT()
			.then((token) => {
				if (!isMounted) {
					return;
				}

				setAccessToken(token);
			})
			.catch((error) => {
				captureException(error);
				notify({
					variant: 'error',
					title: translate('generateWithPrompt.notification.error.title'),
				});
				onClose();
			});

		return () => {
			isMounted = false;
		};
	}, [isOpen, onClose, translate]);

	if (!isOpen) {
		return null;
	}

	const assetTypes: assetType[] = allowDocuments ? ['IMAGE', 'DOCUMENT'] : ['IMAGE'];

	return (
		<CVModal isOpen={Boolean(accessToken)} onClose={onClose}>
			<Login
				authentication={{
					getAccessToken: () => accessToken,
					hideLogout: true,
				}}
			>
				<CompactView
					onSuccess={(assets) => {
						const supportedAssets: unknown[] = [];
						let hasUnsupportedDocuments = false;

						for (const asset of assets) {
							const extension = getAssetExtension(asset);

							if (isUnsupportedDocumentExtension(extension)) {
								hasUnsupportedDocuments = true;
							} else {
								supportedAssets.push(asset);
							}
						}

						if (hasUnsupportedDocuments) {
							notify({
								variant: 'error',
								title: translate(
									'generateWithPrompt.notification.unsupported-document.title',
								),
							});
						}

						const referenceAssets: ReferenceAsset[] = [];

						for (const asset of supportedAssets) {
							try {
								referenceAssets.push(parseAsset(asset));
							} catch (error) {
								captureException(error);
								notify({
									variant: 'error',
									title: translate('generateWithPrompt.notification.error.title'),
								});
								onClose();

								return;
							}
						}

						if (referenceAssets.length > 0) {
							onSelect(referenceAssets);
						}

						onClose();
					}}
					assetTypes={assetTypes}
					mode={maxAssets !== undefined && maxAssets > 1 ? 'MultiSelect' : 'SingleSelect'}
				/>
			</Login>
		</CVModal>
	);
}
